// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useState } from 'react';

/**
 * A hook to trigger rerenders at specified intervals, e.g. every minute, every hour, every day etc.
 * @param timeToFirstRerender time in milliseconds until the first re-render
 * @param interval time in milliseconds for how often the re-render should trigger
 * @param rerenderCallback optional callback to call when re-render is triggered
 * @returns seed for any useEffects that should re-render
 */
export const useRerenderAtIntervals = (
  timeToFirstRerender: number,
  interval: number,
  rerenderCallback?: (seed: number) => void
): number | NodeJS.Timeout => {
  const [seed, forceRerender] = useState(0);

  useEffect(() => {
    const rerenderInterval = (): NodeJS.Timeout => {
      return setInterval(() => {
        const newSeed = Date.now();
        forceRerender(newSeed);
        rerenderCallback?.(newSeed);
      }, interval);
    };
    let intervalHandle: number | NodeJS.Timeout;

    const timeoutHandle = setTimeout(() => {
      intervalHandle = rerenderInterval();
    }, timeToFirstRerender);

    return () => {
      clearInterval(intervalHandle);
      clearTimeout(timeoutHandle);
    };
  }, [rerenderCallback]);

  return seed;
};
